import type { RegistryComponentProps } from '@boost-sd/components-registry';
import { registryComponent } from '@boost-sd/components-registry/registry';
import type { DeepPartial } from '@types';
import { isCollectionPage } from '@utils';
import { merge } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useImperativeHandle, useState } from 'react';

import type { AdditionalElementThemeSettings } from '../types';

const AdditionalElementThemeSettingsContext = createContext<AdditionalElementThemeSettings | null>(
  null
);

export type AdditionalElementThemeSettingsProviderProps = PropsWithChildren<{
  defaultSettings?: Partial<AdditionalElementThemeSettings>;
}>;

export const defaultAdditionalElementSettings = {
  showLimitList: [8, 16, 24, 32, 48],
  sortingList: [
    'relevance',
    'best-selling',
    'manual',
    'price-ascending',
    'price-descending',
    'title-ascending',
    'title-descending',
    'created-descending',
    'created-ascending',
  ],
};

export const AdditionalElementThemeSettingsProvider = registryComponent(
  'AdditionalElementThemeSettingsProvider',
  ({
    children,
    defaultSettings,
    helpersRef,
  }: RegistryComponentProps<
    AdditionalElementThemeSettingsProviderProps,
    {
      reloadSettings: (settings?: DeepPartial<AdditionalElementThemeSettings>) => void;
      getSettings: () => AdditionalElementThemeSettings;
    }
  >) => {
    const { customSortingList } = window.boostSDAppConfig?.additionalElementSettings || {};

    const sortingList = customSortingList
      ? customSortingList.split('|')
      : defaultAdditionalElementSettings.sortingList;

    const collectionPage = isCollectionPage();

    const { showLimitList } = defaultAdditionalElementSettings;

    const settingsFromWindow = window?.boostSDAppConfig?.themeSettings?.additionalElements || {};
    const fallbackSettings = {
      toolbar: {
        showLimitList,
        sortingList: sortingList.filter((value: string) => collectionPage || value !== 'manual'),
      },
      collectionHeader: {},
      pagination: {},
    };

    const [settings, setSettings] = useState(
      merge({}, settingsFromWindow, fallbackSettings, defaultSettings, {
        // pagination: {
        //   paginationType: 'infinite_scroll',
        // },
      })
    );

    useImperativeHandle(helpersRef, () => ({
      getSettings() {
        return settings;
      },
      reloadSettings(settings) {
        const settingsFromWindow =
          window?.boostSDAppConfig?.themeSettings?.additionalElements || {};

        setSettings(merge({}, settingsFromWindow, fallbackSettings, defaultSettings, settings));
      },
    }));

    return (
      <AdditionalElementThemeSettingsContext.Provider value={settings}>
        {children}
      </AdditionalElementThemeSettingsContext.Provider>
    );
  }
);

export const useAdditionalElementThemeSettings = () => {
  const context = useContext(AdditionalElementThemeSettingsContext);
  if (!context) {
    throw Error('Use useAdditionalElementThemeSettings in AdditionalElementThemeSettingsProvider');
  }

  if (!context.pagination) {
    // eslint-disable-next-line no-console
    console.error('Debug PFSN-48594: pagination is null', context);
  }

  return {
    ...context,
    pagination: context.pagination || {},
  };
};
